// import knownUser from '../../../public/testdata/knownUser.js';
import { merge } from '@/helpers/object';
import { removeUserSelectedHeadhunter, removeUserSelectedJob } from '@/helpers/user-selections';
import { removeLastHeadhunterSearchFilters } from '@/helpers/last-search';

// @ts-ignore
const USER_INFO = window.appSetup?.user?.id ? window.appSetup.user : null;

const state = () => ({
  info: USER_INFO,
  numbers: {
    job: {
      bookmarks: 0,
      savedSearches: 0,
    },

    headhunter: {
      bookmarks: 0,
    },
  },

  setPaymentData: null,
  usaReport: null,
  usaReportRate: 0,
  usaReportRateLoading: false,
  usaReportHasBeenSeen: false, // session variable to hide red dot in menu indicating if the report has been visible
  notifications: [],
});

// mutations
const mutations = {
  setInfo(currentState, value) {
    currentState.info = merge(currentState.info, value);
  },
  setNumbers(currentState, newValue) {
    currentState.numbers = merge(currentState.numbers, newValue);
  },
  setUsaReportHasBeenSeen(currentState, boolean) {
    currentState.usaReportHasBeenSeen = boolean;
  },
  setNotifications(currentState, payload) {
    currentState.notifications = payload;
  },
  setMembership(currentState, payload) {
    currentState.info = merge(currentState.info, payload);
  },
};

// actions
const actions = {
  init({ commit, dispatch }, info) {
    if (!info?.id) return;
    commit('setInfo', info);
    dispatch('fetchNumbers');
  },

  fetch({ commit, dispatch }) {
    return this.$api.get(this.getters.getApiLink('user.me')).then(({ data }) => {
      if (!data?.id) {
        throw new Error('User id is undefined');
      }

      const formattedData = {
        ...data,
        get fullName() {
          return [this.firstName, this.lastName].join(' ').trim();
        },
      };

      commit('setInfo', JSON.parse(JSON.stringify(formattedData)));
      dispatch('fetchNumbers');

      return formattedData;
    });
  },

  fetchNumbers({ commit }) {
    return this.$api
      .get(this.getters.getApiLink('user.collectNumbers'))
      .then(
        ({ data: { savedSearches, watchlistedJobs: bookmarkedJobs, bookmarkedHeadhunters } }) => {
          commit('setNumbers', {
            job: {
              bookmarks: bookmarkedJobs,
              savedSearches,
            },

            headhunter: {
              bookmarks: bookmarkedHeadhunters,
            },
          });
        },
      );
  },

  destroy({ commit }) {
    commit('setInfo', null);
    commit('setHasUsedCVCheck', false);
    commit('setIsCVCheckLoading', false);
  },

  clearLoggedUserData() {
    removeLastHeadhunterSearchFilters();
    removeUserSelectedJob();
    removeUserSelectedHeadhunter();
  },
  logout({ rootState, rootGetters }) {
    const currentCountry = rootGetters['country/currentCountry'];
    const countryCode =
      currentCountry?.countryCode?.toLowerCase() || currentCountry?.toLowerCase() || 'us';

    window.location.href =
      countryCode === 'us'
        ? rootState.data?.links?.navigation.logout
        : rootState.data?.links?.navigation.logout + '?country=' + countryCode;
  },
  fetchNotifications({ commit }) {
    return this.$api.get(this.getters.getApiLink('user.notifications')).then(({ data }) => {
      commit('setNotifications', data?.notifications);
      return data?.notifications || [];
    });
  },
  fetchMembership({ commit }) {
    return this.$api.get(this.getters.getApiLink('user.membershipStatus')).then(({ data }) => {
      commit('setMembership', data || {});
      return data || {};
    });
  },
  refreshToken() {
    this.$api.post(this.getters.getApiLink('user.refreshToken'));
  },
};
// actions end

const getters = {
  info: (currentState) => {
    // todo: refactor, remove the code below completely and unify the format with backend
    if (!currentState.info) {
      return currentState.info;
    }

    const result = {
      extraData: null,
    };

    const formattedKeys = {
      id: 'id',
      academicDegree: 'academicDegree',
      name: 'firstName',
      surname: 'lastName',
      fullName: 'fullName',
      email: 'emailAddress',

      isPremium: 'isPremium',
      isSocial: 'isSocial',
      role: 'role',
      authenticationStatus: 'authenticationStatus',
      hasRightToViewPremiumMarketing: 'hasRightToViewPremiumMarketing',
      hasRightToViewBlockedPremiumMessage: 'hasRightToViewBlockedPremiumMessage',
      firstLoginAfterActivationByUser: 'firstLoginAfterActivationByUser',
      hasValidContract: 'hasValidContract',
      hasValidMembership: 'hasValidMembership',
      hasValidOrEndedContract: 'hasValidOrEndedContract',
      hasFreePremiumMembership: 'hasFreePremiumMembership',
      freePremiumMembership: 'freePremiumMembership',
      image: 'photoPath',
      category: 'category',
      hasUsedCVCheck: 'hasUsedCVCheck',
      searchCountry: 'searchCountry',
      membershipCancellation: 'membershipCancellation',
      sendFreeJobsToShop: 'sendFreeJobsToShop',
    };
    const formattedKeyEntries = Object.entries(formattedKeys);
    Object.assign(
      result,
      Object.fromEntries(
        formattedKeyEntries.map(([key, infoKey]) => [key, currentState.info[infoKey]]),
      ),
    );

    const excludeKeys = formattedKeyEntries.map(([, key]) => key);
    result.extraData = Object.fromEntries(
      Object.entries(currentState.info).filter(([key]) => !excludeKeys.includes(key)),
    );

    // membership
    (() => {
      const keys = {
        title: 'title',
        duration: 'term',
        pricePerUnit: 'price',
        renewalOrCancellationText: 'renewText',
        periodOfNotice: 'periodOfNotice',
        cancellationNoticeDate: 'cancellationNoticeDate',
        cancellationPossibleUntilDate: 'cancellationPossibleUntilDate',
        effectiveCancellationDate: 'effectiveCancellationDate',
        cancellationRequestDate: 'cancellationRequestDate',
        cancellationRequestTime: 'cancellationRequestTime',
        isPeriodOfNoticeZero: 'isPeriodOfNoticeZero',
      };

      const { contract } = result.extraData || {};

      const fn = ([key, formattedKey]) => [formattedKey, contract[key]];
      Object.assign(result, {
        membership: contract ? Object.fromEntries(Object.entries(keys).map(fn)) : null,
      });
    })();

    // address
    (() => {
      const keys = {
        firstName: 'name',
        lastName: 'surname',
        streetName: 'street',
        streetNumber: 'streetNumber',
        addressAddition: 'building',
        zipCode: 'zip',
        cityName: 'city',
        countryCode: 'country',
      };

      const obj = result.extraData?.billingAddress || {};

      const fn = ([key, formattedKey]) => [formattedKey, obj[key]];
      const address = Object.fromEntries(Object.entries(keys).map(fn));

      Object.assign(result, { billingAddress: obj ? address : null });
    })();

    return result;
  },
  membership: (currentState, currentGetters) => currentGetters.info?.membership || null,
  billingAddress: (currentState, currentGetters) => currentGetters.info?.billingAddress || null,
  invoices: (currentState, currentGetters) => currentGetters.info?.invoices || null,
  isAuthenticated: (s) => s.info?.authenticationStatus === 'authenticated',
  isLoggedIn: (s) =>
    s.info?.authenticationStatus === 'authenticated' || s.info?.authenticationStatus === 'known',
  isPremium: (s) =>
    s.info?.isPremium === true &&
    (s.info?.authenticationStatus === 'authenticated' || s.info?.authenticationStatus === 'known'),
  getNumbers: (s) => (key) => s.numbers[key] || {},
  hasUsedCVCheck: (currentState, currentGetters, rootState, rootGetters) =>
    rootGetters['resume/hasUsedCVCheck'] || currentState.info?.hasUsedCVCheck || false,
  usaReportHasBeenSeen: (s) => s.usaReportHasBeenSeen,
  searchCountry: (s) => s.info.searchCountry,
  firstName: (s) => s.info?.firstName,
};

export default {
  namespaced: true,

  mutations,
  state,
  actions,
  getters,
};
